import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  getCsDetail,
  getCsManagers,
  getRejectionReasons,
} from '../../../../../../api/theHive/redeProto';
import {
  CsDetail,
  CsManager,
  RejectionReason,
} from '../../../../../../api/theHive/redeProto/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { RedeProtoOutletContext } from '../../types';

interface UseCustomerSuccessHook {
  managers: CsManager[];
  selectedId: number | undefined;
  selectCs: (id: number) => void;
  detail: CsDetail | undefined;
  detailLoading: boolean;
  reasons: RejectionReason[];
  loading: boolean;
  error: boolean;
}

export default function useCustomerSuccess(): UseCustomerSuccessHook {
  const [managers, setManagers] = useState<CsManager[]>([]);
  const [reasons, setReasons] = useState<RejectionReason[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [detail, setDetail] = useState<CsDetail>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { selectedNoticeId } = useOutletContext<RedeProtoOutletContext>();

  const getData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [managersRes, reasonsRes] = await Promise.all([
        getCsManagers(selectedNoticeId),
        getRejectionReasons(selectedNoticeId),
      ]);

      [managersRes, reasonsRes].forEach((response) => {
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (!response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }
      });

      const managersData = managersRes.data ?? [];
      setManagers(managersData);
      setReasons(reasonsRes.data ?? []);
      setSelectedId(managersData[0]?.id);
    } catch (requestError) {
      setError(true);
      handleSnackbar(getErrorMessage(requestError), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoticeId]);

  useEffect(() => {
    getData();
  }, [getData]);

  /** Busca o detalhe da carteira (polos + credenciadas) do CS selecionado. */
  const loadDetail = useCallback(
    async (id: number) => {
      setDetailLoading(true);
      try {
        const response = await getCsDetail(id, selectedNoticeId);
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        setDetail(response.data);
      } catch (requestError) {
        setDetail(undefined);
        handleSnackbar(getErrorMessage(requestError), true);
      } finally {
        setDetailLoading(false);
      }
    },
    [handleSnackbar, getErrorMessage, selectedNoticeId]
  );

  useEffect(() => {
    if (selectedId != null) {
      loadDetail(selectedId);
    }
  }, [selectedId, loadDetail]);

  return {
    managers,
    selectedId,
    selectCs: setSelectedId,
    detail,
    detailLoading,
    reasons,
    loading,
    error,
  };
}
